import { Application, Container, FederatedPointerEvent } from 'pixi.js';
import { gsap } from 'gsap';
import type { BookCore } from '@/modules/flipcore/BookCore';
import { clientPointToElement } from '@/utils/readerViewportCoordinates';
import { ZoomControlBar } from './ZoomControlBar';
import {
  clampZoom,
  scaleAboutPoint,
  scaleAboutVisualCenter,
  scaleZoomByPinchDistance,
  snapBackPosition,
  type Point
} from './zoomMath';

const MIN_ZOOM = 2.2;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;
const ENTER_ZOOM = 1.5;
const ZOOM_ANIM = 0.3;
const SNAP_ANIM = 0.5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DIST = 16;
const BAR_TOP_MARGIN = 24;
const WHEEL_ZOOM_STEP = 0.15;
const PINCH_ENTER_RATIO = 1.02;

interface Options {
  book: BookCore;
  app: Application;
  stage: Container;
  /** 缩放模式进入/退出时通知外层 UI 同步状态。 */
  onStateChange?: (active: boolean) => void;
}

export class BookZoomController {
  private readonly book: BookCore;
  private readonly app: Application;
  private readonly stage: Container;
  private readonly bar: ZoomControlBar;
  private readonly onStateChange?: (active: boolean) => void;

  private active = false;
  private zoom = 1;
  private baseScale = 1;
  private basePositionX = 0;
  private basePositionY = 0;
  private exitCall: gsap.core.Tween | null = null;

  private lastTapTime = 0;
  private lastTapX = 0;
  private lastTapY = 0;

  private dragging = false;
  private dragPointerId: number | null = null;
  private dragStartStageX = 0;
  private dragStartStageY = 0;
  private rootStartX = 0;
  private rootStartY = 0;

  private readonly touchPointers = new Map<number, Point>();
  private pinchPointerIds: [number, number] | null = null;
  private pinching = false;
  private pinchInitialDistance = 0;
  private pinchLastDistance = 0;
  private pinchLastCenter: Point | null = null;
  private pinchSuppressedUntilRelease = false;
  private pinchDisabledBookInteraction = false;

  constructor({ book, app, stage, onStateChange }: Options) {
    this.book = book;
    this.app = app;
    this.stage = stage;
    this.onStateChange = onStateChange;

    this.bar = new ZoomControlBar({
      onZoomIn: () => this.stepZoom(ZOOM_STEP),
      onZoomOut: () => this.stepZoom(-ZOOM_STEP),
      onReset: () => this.exit(),
      onClose: () => this.exit()
    });
    this.bar.hide();
    this.stage.addChild(this.bar);
    this.layoutBar();

    this.stage.on('pointertap', this.handleTap);
    this.stage.on('pointerdown', this.handleDown);
    this.app.renderer.on('resize', this.handleResize);
    document.addEventListener('wheel', this.handleWheel, { passive: false, capture: true });
    window.addEventListener('pointermove', this.handleTouchWindowMove, { passive: false });
    window.addEventListener('pointerup', this.handleTouchWindowEnd);
    window.addEventListener('pointercancel', this.handleTouchWindowEnd);
  }

  /** 缩放模式是否激活（供外层判断是否屏蔽点击翻页）。 */
  isActive(): boolean {
    return this.active;
  }

  /** 切换缩放模式（与底部控制条放大/恢复按钮、双击书本共用）。 */
  toggle(): void {
    if (this.active) this.exit();
    else this.enter();
  }

  private get root(): Container | undefined {
    return this.book.root;
  }

  private get canvasElement(): HTMLElement {
    return (this.app.renderer.events.domElement as HTMLElement | null) ?? this.app.canvas;
  }

  /**
   * 把指针的全局（屏幕）坐标转换到 stage 局部坐标。
   * 视口、回弹与拖拽位移都在此坐标系下计算——即「可视口相对 stage」。
   * elementContainer 是 stage 的单位变换子节点，故 stage 局部坐标与 root.position 所在空间一致。
   */
  private toStageLocal(global: Point): Point {
    return this.stage.toLocal(global);
  }

  /** 把浏览器 client 坐标转换为 stage 局部坐标，供原生滚轮事件使用。 */
  private clientToStageLocal(clientX: number, clientY: number): Point {
    const global = clientPointToElement(
      this.canvasElement,
      clientX,
      clientY,
      this.app.screen.width,
      this.app.screen.height
    );
    return this.toStageLocal(global);
  }

  private layoutBar() {
    const { height } = this.app.screen;
    this.bar.position.set(-this.bar.barWidth / 2, -height / 2 + BAR_TOP_MARGIN);
  }

  private readonly handleResize = () => {
    this.layoutBar();
    if (this.active) this.exit(); // 缩放模式中 resize 直接退出，避免与外层布局抢占 root
  };

  /**
   * 视口提交新尺寸前同步终止缩放状态和旧补间。
   * 不恢复旧 base transform；外层会立即用同一尺寸快照写入新的书本布局。
   */
  prepareForViewportResize(): void {
    const root = this.root;
    const wasActive = this.active;

    this.active = false;
    this.dragging = false;
    this.stopWindowDragging();
    this.endPinch(false);
    this.cancelExitCall();
    this.bar.hide();

    if (root) {
      gsap.killTweensOf(root.scale);
      gsap.killTweensOf(root.position);
    }

    this.book.setInteractionEnabled(true);
    if (wasActive) this.onStateChange?.(false);
  }

  /** 判断 stage 点是否落在当前可见书本区域内。 */
  private isPointInsideVisibleBook(point: Point): boolean {
    const root = this.root;
    if (!root) return false;

    const { width, height, offsetX, offsetY } = this.book.visibleLayout;
    const scale = root.scale.x;
    const left = root.position.x + offsetX * scale;
    const top = root.position.y + offsetY * scale;
    const right = left + width * scale;
    const bottom = top + height * scale;

    return point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
  }

  private readonly handleTap = (e: FederatedPointerEvent) => {
    const now = performance.now();
    const p = this.toStageLocal(e.global);
    if (!this.isPointInsideVisibleBook(p)) {
      this.lastTapTime = 0;
      return;
    }

    const dist = Math.hypot(p.x - this.lastTapX, p.y - this.lastTapY);
    if (now - this.lastTapTime < DOUBLE_TAP_MS && dist < DOUBLE_TAP_DIST) {
      this.lastTapTime = 0;
      this.toggle();
      return;
    }
    this.lastTapTime = now;
    this.lastTapX = p.x;
    this.lastTapY = p.y;
  };

  private enter(animate = true) {
    if (!this.root) return;
    this.cancelExitCall();
    gsap.killTweensOf(this.root.scale);
    gsap.killTweensOf(this.root.position);
    this.active = true;
    this.captureBaseTransform();
    this.book.setInteractionEnabled(false);
    this.bar.show();
    this.onStateChange?.(true);
    this.applyZoom(ENTER_ZOOM, animate);
  }

  private exit() {
    this.active = false;
    this.onStateChange?.(false);
    this.dragging = false;
    this.stopWindowDragging();
    this.bar.hide();
    this.restoreBaseTransform(true);
    this.cancelExitCall();
    this.exitCall = gsap.delayedCall(ZOOM_ANIM, () => {
      this.book.setInteractionEnabled(true);
      this.exitCall = null;
    });
  }

  /** 记录进入缩放前的 100% 展示位，退出时按该位置居中恢复。 */
  private captureBaseTransform() {
    if (!this.root) return;
    this.baseScale = this.root.scale.x;
    this.basePositionX = this.root.position.x;
    this.basePositionY = this.root.position.y;
  }

  /** 恢复到进入缩放前的 100% 展示位，避免保留拖拽后的偏移。 */
  private restoreBaseTransform(animate: boolean) {
    const root = this.root;
    if (!root) return;

    this.zoom = 1;
    this.bar.setZoom(MIN_ZOOM, MIN_ZOOM, MAX_ZOOM);
    gsap.killTweensOf(root.scale);
    gsap.killTweensOf(root.position);
    if (animate) {
      gsap.to(root.scale, {
        x: this.baseScale,
        y: this.baseScale,
        duration: ZOOM_ANIM,
        ease: 'power2.out'
      });
      gsap.to(root.position, {
        x: this.basePositionX,
        y: this.basePositionY,
        duration: ZOOM_ANIM,
        ease: 'power2.out'
      });
      return;
    }
    root.scale.set(this.baseScale);
    root.position.set(this.basePositionX, this.basePositionY);
  }

  private cancelExitCall() {
    this.exitCall?.kill();
    this.exitCall = null;
  }

  private stepZoom(delta: number) {
    this.applyZoom(this.zoom + delta, true);
  }

  /** 缩放模式下拦截滚轮：滚动只改变缩放比例，不继续触发翻页。 */
  private readonly handleWheel = (event: WheelEvent) => {
    if (!this.active || !this.isWheelOnCanvas(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const delta = event.deltaY < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP;
    if (delta < 0 && this.zoom <= MIN_ZOOM + 1e-6) {
      this.exit();
      return;
    }

    this.applyZoom(this.zoom + delta, true, this.clientToStageLocal(event.clientX, event.clientY));
  };

  /** 判断滚轮事件是否发生在当前 Pixi canvas 上，避免影响页面其他区域。 */
  private isWheelOnCanvas(event: WheelEvent): boolean {
    const path = event.composedPath();
    return path.includes(this.canvasElement);
  }

  private applyZoom(targetZoom: number, animate: boolean, anchor?: Point) {
    const root = this.root;
    if (!root) return;

    const minZoom = this.active ? MIN_ZOOM : 1;
    this.zoom = clampZoom(targetZoom, minZoom, MAX_ZOOM);
    this.bar.setZoom(this.zoom, MIN_ZOOM, MAX_ZOOM);

    const newScale = this.baseScale * this.zoom;
    const { width: contentW, height: contentH, offsetX, offsetY } = this.book.visibleLayout;
    const currentPosition = { x: root.position.x, y: root.position.y };
    // 滚轮用鼠标悬停点为支点；按钮缩放沿用可见内容中心，避免书本整体漂移。
    const scaled = anchor
      ? scaleAboutPoint(currentPosition, anchor, root.scale.x, newScale)
      : scaleAboutVisualCenter(
          currentPosition,
          contentW,
          contentH,
          offsetX,
          offsetY,
          root.scale.x,
          newScale
        );
    const target = this.clampToViewport(scaled, newScale);

    gsap.killTweensOf(root.scale);
    gsap.killTweensOf(root.position);
    if (animate) {
      gsap.to(root.scale, { x: newScale, y: newScale, duration: ZOOM_ANIM, ease: 'power2.out' });
      gsap.to(root.position, { x: target.x, y: target.y, duration: ZOOM_ANIM, ease: 'power2.out' });
    } else {
      root.scale.set(newScale);
      root.position.set(target.x, target.y);
    }
  }

  /** 将 root.position 贴合到视口内，按可见跨页尺寸与 stage 矩形相交结果计算。 */
  private clampToViewport(position: { x: number; y: number }, scale: number) {
    const { width, height } = this.app.screen;
    const { width: contentW, height: contentH, offsetX, offsetY } = this.book.visibleLayout;
    return snapBackPosition({
      position,
      contentW,
      contentH,
      scale,
      viewportW: width,
      viewportH: height,
      contentOffsetX: offsetX,
      contentOffsetY: offsetY
    });
  }

  private readonly handleDown = (e: FederatedPointerEvent) => {
    if (e.pointerType === 'touch') {
      this.touchPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.touchPointers.size >= 2) {
        this.beginPinch();
        return;
      }
    }

    if (!this.active || !this.root) return;
    gsap.killTweensOf(this.root.position);
    const startPoint = this.clientToStageLocal(e.clientX, e.clientY);
    this.dragging = true;
    this.dragPointerId = e.pointerId;
    this.dragStartStageX = startPoint.x;
    this.dragStartStageY = startPoint.y;
    this.rootStartX = this.root.position.x;
    this.rootStartY = this.root.position.y;
    window.addEventListener('pointermove', this.handleWindowMove);
    window.addEventListener('pointerup', this.handleWindowUp);
  };

  private beginPinch() {
    if (this.pinching || this.pinchSuppressedUntilRelease) return;
    if (!this.active && !this.book.isInteractionEnabled()) return;

    const pointerIds = [...this.touchPointers.keys()].slice(0, 2);
    if (pointerIds.length < 2) return;

    this.pinchPointerIds = [pointerIds[0], pointerIds[1]];
    const metrics = this.getPinchMetrics();
    if (!metrics || metrics.distance <= 0) {
      this.pinchPointerIds = null;
      return;
    }

    this.dragging = false;
    this.stopWindowDragging();
    this.pinchDisabledBookInteraction = !this.active;
    if (this.pinchDisabledBookInteraction) {
      this.book.setInteractionEnabled(false);
    }
    this.pinching = true;
    this.pinchInitialDistance = metrics.distance;
    this.pinchLastDistance = metrics.distance;
    this.pinchLastCenter = metrics.center;
  }

  private getPinchMetrics(): { distance: number; center: Point } | null {
    if (!this.pinchPointerIds) return null;

    const first = this.touchPointers.get(this.pinchPointerIds[0]);
    const second = this.touchPointers.get(this.pinchPointerIds[1]);
    if (!first || !second) return null;

    return {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      center: this.clientToStageLocal((first.x + second.x) / 2, (first.y + second.y) / 2)
    };
  }

  private readonly handleTouchWindowMove = (event: PointerEvent) => {
    if (event.pointerType !== 'touch' || !this.touchPointers.has(event.pointerId)) return;

    this.touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (!this.pinching || this.pinchSuppressedUntilRelease) return;

    const metrics = this.getPinchMetrics();
    if (!metrics || !this.pinchLastCenter) return;

    event.preventDefault();

    if (!this.active) {
      if (metrics.distance < this.pinchInitialDistance * PINCH_ENTER_RATIO) return;

      this.enter(false);
      this.pinchLastDistance = metrics.distance;
      this.pinchLastCenter = metrics.center;
      return;
    }

    if (this.zoom <= MIN_ZOOM + 1e-6 && metrics.distance < this.pinchLastDistance) {
      this.pinchSuppressedUntilRelease = true;
      this.exit();
      return;
    }

    const root = this.root;
    if (!root) return;

    root.position.set(
      root.position.x + metrics.center.x - this.pinchLastCenter.x,
      root.position.y + metrics.center.y - this.pinchLastCenter.y
    );
    this.applyZoom(
      scaleZoomByPinchDistance(this.zoom, this.pinchLastDistance, metrics.distance),
      false,
      metrics.center
    );
    this.pinchLastDistance = metrics.distance;
    this.pinchLastCenter = metrics.center;
  };

  private readonly handleTouchWindowEnd = (event: PointerEvent) => {
    if (event.pointerType !== 'touch' || !this.touchPointers.delete(event.pointerId)) return;

    if (this.pinching && this.pinchPointerIds?.includes(event.pointerId)) {
      this.endPinch();
    }
    if (this.touchPointers.size === 0) {
      this.pinchSuppressedUntilRelease = false;
    }
  };

  private endPinch(restoreInteraction = true) {
    const shouldRestoreInteraction =
      restoreInteraction && this.pinchDisabledBookInteraction && !this.active && !this.exitCall;

    this.pinching = false;
    this.pinchPointerIds = null;
    this.pinchInitialDistance = 0;
    this.pinchLastDistance = 0;
    this.pinchLastCenter = null;
    this.pinchDisabledBookInteraction = false;

    if (shouldRestoreInteraction) {
      this.book.setInteractionEnabled(true);
    }
  }

  /** 使用 DOM 屏幕坐标增量同步拖拽位置，离开 stage 后仍稳定。 */
  private syncDragPositionFromClient(clientX: number, clientY: number) {
    if (!this.dragging || !this.root) return;
    const currentPoint = this.clientToStageLocal(clientX, clientY);
    const dx = currentPoint.x - this.dragStartStageX;
    const dy = currentPoint.y - this.dragStartStageY;
    this.root.position.set(this.rootStartX + dx, this.rootStartY + dy);
  }

  private readonly handleWindowMove = (e: PointerEvent) => {
    if (!this.dragging || !this.root) return;
    if (this.dragPointerId !== null && e.pointerId !== this.dragPointerId) return;
    this.syncDragPositionFromClient(e.clientX, e.clientY);
  };

  private readonly handleWindowUp = (e: PointerEvent) => {
    if (this.dragPointerId !== null && e.pointerId !== this.dragPointerId) return;
    this.syncDragPositionFromClient(e.clientX, e.clientY);
    this.handleDragRelease();
  };

  /** 停止窗口级拖拽监听，避免离开 stage 后残留事件。 */
  private stopWindowDragging() {
    window.removeEventListener('pointermove', this.handleWindowMove);
    window.removeEventListener('pointerup', this.handleWindowUp);
    this.dragPointerId = null;
  }

  /** 松手时才根据拖拽方向执行越界回弹。 */
  private handleDragRelease() {
    if (!this.dragging || !this.root) return;
    this.dragging = false;
    this.stopWindowDragging();
    const target = this.clampToViewport(
      { x: this.root.position.x, y: this.root.position.y },
      this.root.scale.x
    );
    gsap.killTweensOf(this.root.position);
    gsap.to(this.root.position, {
      x: target.x,
      y: target.y,
      duration: SNAP_ANIM,
      ease: 'power3.out'
    });
  }

  destroy() {
    this.stage.off('pointertap', this.handleTap);
    this.stage.off('pointerdown', this.handleDown);
    this.app.renderer.off('resize', this.handleResize);
    document.removeEventListener('wheel', this.handleWheel, true);
    window.removeEventListener('pointermove', this.handleTouchWindowMove);
    window.removeEventListener('pointerup', this.handleTouchWindowEnd);
    window.removeEventListener('pointercancel', this.handleTouchWindowEnd);
    this.touchPointers.clear();
    this.endPinch(false);
    this.stopWindowDragging();
    this.cancelExitCall();
    if (this.root) {
      gsap.killTweensOf(this.root.scale);
      gsap.killTweensOf(this.root.position);
    }
    this.bar.destroy({ children: true });
  }
}
